/**
 * Check if a user's profile is complete
 * @param {Object} user - The user object
 * @returns {boolean} - True if profile is complete, false otherwise
 */
export function isProfileComplete(user) {
  if (!user) return false;

  // Required: name, email, phone. Address is optional.
  const hasBasicInfo = 
    user.name && 
    user.email && 
    user.phone;

  return hasBasicInfo;
}

/**
 * Get a list of missing profile fields
 * @param {Object} user - The user object
 * @returns {Array<string>} - Array of missing field names
 */
export function getMissingProfileFields(user) {
  if (!user) return [];

  const missing = [];

  // Required: name, email, phone (address optional)
  if (!user.name) missing.push('Name');
  if (!user.email) missing.push('Email');
  if (!user.phone) missing.push('Phone');

  return missing;
}

/**
 * Calculate profile completion percentage
 * @param {Object} user - The user object
 * @returns {number} - Completion percentage (0-100)
 */
export function getProfileCompletionPercentage(user) {
  if (!user) return 0;

  const totalFields = 3; // name, email, phone (address optional)
  let completedFields = 0;

  if (user.name) completedFields++;
  if (user.email) completedFields++;
  if (user.phone) completedFields++;

  return Math.round((completedFields / totalFields) * 100);
}

/**
 * Get the current step number for profile completion (1-2)
 * Step 1: Basic Info, Step 2: Shipping Info
 * @param {Object} user - The user object
 * @returns {number} - Current step number (1 or 2)
 */
export function getCurrentProfileStep(user) {
  if (!user) return 1;

  // Required: name, email, phone (address optional)
  const hasBasicInfo = user.name && user.email && user.phone;
  if (!hasBasicInfo) return 1;

  return 2; // All steps complete (step 2 / shipping section is hidden)
}

/**
 * Check if a specific step is complete
 * @param {Object} user - The user object
 * @param {number} step - Step number (1 or 2)
 * @returns {boolean} - True if step is complete
 */
export function isStepComplete(user, step) {
  if (!user) return false;

  switch (step) {
    case 1: // Basic Info (name, email, phone; address optional)
      return !!(user.name && user.email && user.phone);
    case 2: // Shipping info no longer required (section hidden)
      return true;
    default:
      return false;
  }
}
